import '../../styles/fonts.css';
import React, { useEffect, useState } from 'react';
import { ChatItem } from './ChatItem';
import { SearchButtons } from './SearchButtons';
import { useAuth } from '../../context/AuthContext';
import { Chat, Message } from '../../types/chat';
import { useChatsContext } from '../../context/ChatsContext';
import { useSocketContext } from '../../context/SocketContext';
import { ExitIcon, KeyIcon, QuestionIcon } from '../../assets/SvgIcons';
import { GroupChat } from '../../services/api';

interface ChatsListProps {
  onChatSelect: (chat: Chat) => void;
  selectedChatId?: number;
  isDesktop?: boolean;
  onRandomSearch?: () => void;
  onSearchByNumber?: (number: string) => void;
}

export const ChatsList: React.FC<ChatsListProps> = ({
  onChatSelect,
  selectedChatId,
  isDesktop = false,
  onRandomSearch,
  onSearchByNumber,
}) => {
  const { chats, groupChats, isLoading, loadChats, setChats } = useChatsContext();
  const { user, logout } = useAuth();
  const socket = useSocketContext();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  useEffect(() => {
    if (!socket || !user) return;

    console.log('ChatsList: Подписываемся на события сокета');

    const handleChatCreated = (data: any) => {
      console.log('🎯 ChatsList: Получено событие chat_created:', data);

      // ✅ Игнорируем групповые чаты - они обрабатываются отдельно
      if (data.isGroupChat) {
        console.log('⚠️ Пропускаем групповой чат в chat_created');
        return;
      }

      setChats((prevChats) => {
        const exists = prevChats.find((c) => c.id === data.chatId);
        if (exists) {
          console.log('⚠️ Чат уже существует, пропускаем');
          return prevChats;
        }

        const newChat: Chat = {
          id: data.chatId,
          companionNumber: data.companionNumber,
          companionId: data.companionId,
          unreadCount: data.unreadCount || 0,
          isNew: data.isNew || true,
          createdAt: data.createdAt,
          lastMessage: data.lastMessage || undefined,
          messages: data.messages || [],
          isGroupChat: false, // ✅ Явно указываем
        };

        console.log('✅ ChatsList: Добавляем новый чат:', newChat);
        socket.joinChat(data.chatId);

        return [newChat, ...prevChats];
      });
    };

    const handleNewMessage = (message: Message) => {
      console.log('💬 ChatsList: Получено новое сообщение:', message);

      if (message.chatId && message.chatId !== 0) {
        const currentUserNumber = user?.userNumber;
        const isOwnMessage = message.senderNumber === currentUserNumber;

        setChats((prevChats) => {
          let updatedChats = [...prevChats];
          let targetChatIndex = updatedChats.findIndex((chat) => chat.id === message.chatId);

          if (targetChatIndex !== -1) {
            const targetChat = updatedChats[targetChatIndex];
            const updatedChat = {
              ...targetChat,
              lastMessage: {
                preview: message.content,
                time: message.timestamp,
                senderNumber: message.senderNumber,
                isOwn: isOwnMessage,
              },
              unreadCount: isOwnMessage ? targetChat.unreadCount : targetChat.unreadCount + 1,
            };
            updatedChats.splice(targetChatIndex, 1);
            updatedChats.unshift(updatedChat);
          }
          return updatedChats;
        });
      }
    };

    const handleNewGroupMessage = (message: Message) => {
      console.log('💬 ChatsList: Получено новое групповое сообщение:', message);

      if (message.groupId) {
        const currentUserNumber = user?.userNumber;
        const isOwnMessage = message.senderNumber === currentUserNumber;

        setChats((prevChats) => {
          let updatedChats = [...prevChats];
          let targetChatIndex = updatedChats.findIndex((chat) => chat.id === message.groupId && chat.isGroupChat);

          if (targetChatIndex !== -1) {
            const targetChat = updatedChats[targetChatIndex];
            const updatedChat = {
              ...targetChat,
              lastMessage: {
                preview: message.content,
                time: message.timestamp,
                senderNumber: message.senderNumber,
                isOwn: isOwnMessage,
              },
              unreadCount: isOwnMessage ? targetChat.unreadCount : targetChat.unreadCount + 1,
            };
            updatedChats.splice(targetChatIndex, 1);
            updatedChats.unshift(updatedChat);
          }
          return updatedChats;
        });
      }
    };

    const handleMatchFound = (chat: Chat) => {
      console.log('🎯 ChatsList: Найден матч:', chat);

      setChats((prevChats) => {
        const exists = prevChats.find((c) => c.id === chat.id);
        if (exists) return prevChats;

        console.log('✅ ChatsList: Добавляем чат из матча:', chat);
        socket.joinChat(chat.id);
        return [chat, ...prevChats];
      });
    };

    const handleChatDeleted = (data: { chatId: number; deletedBy: number; deletedByUserNumber?: string }) => {
      console.log('🗑️ ChatsList: Чат удален:', data);

      setChats((prevChats) => {
        const updatedChats = prevChats.filter((chat) => chat.id !== data.chatId);
        console.log(`✅ Чат ${data.chatId} удален из списка`);
        return updatedChats;
      });
    };

    const handleChatCleared = (data: { chatId: number; clearedBy: number; clearedByUserNumber: string }) => {
      console.log('🧹 ChatsList: Чат очищен:', data);

      setChats((prevChats) => {
        return prevChats.map((chat) => {
          if (chat.id === data.chatId) {
            return {
              ...chat,
              lastMessage: undefined,
            };
          }
          return chat;
        });
      });
    };

    const removeChatCreated = socket.on('chat_created', handleChatCreated);
    const removeNewMessage = socket.on('new_message', handleNewMessage);
    const removeNewGroupMessage = socket.on('new_group_message', handleNewGroupMessage);
    const removeMatchFound = socket.on('match_found', handleMatchFound);
    const removeChatDeleted = socket.on('chat_deleted', handleChatDeleted);
    const removeChatCleared = socket.on('chat_cleared', handleChatCleared);

    // Cleanup
    return () => {
      console.log('ChatsList: Отписываемся от событий сокета');
      removeChatCreated();
      removeNewMessage();
      removeNewGroupMessage();
      removeMatchFound();
      removeChatDeleted();
      removeChatCleared();
    };
  }, [socket, user, setChats]);

  useEffect(() => {
    console.log('ChatsList: chats updated, count:', chats.length);
    console.log(
      'ChatsList: actual chats:',
      chats.map((c) => ({ id: c.id, number: c.companionNumber })),
    );
    console.log('ChatsList: groupChats updated, count:', groupChats.length);
    console.log(
      'ChatsList: actual groupChats:',
      groupChats.map((g) => ({ id: g.id, name: g.name })),
    );
  }, [chats, groupChats]);

  const systemChat: Chat = {
    id: 0,
    companionNumber: 'Тёрка',
    companionId: 0,
    unreadCount: 0,
    isNew: false,
    createdAt: new Date().toISOString(),
  };

  const allChats = [...chats].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          fontFamily: 'Tiny5, monospace',
          fontSize: '14px',
        }}
      >
        ЗАГРУЗКА...
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        backgroundColor: '#ffffff',
        fontFamily: 'monospace',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      {!isDesktop && (
        <div
          style={{
            backgroundColor: '#000',
            color: '#fff',
            padding: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '30px',
            border: '2px solid #e8e8e8',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                backgroundColor: '#000',
                color: '#FFF',
                fontFamily: 'Tiny5, monospace',
                fontSize: '19px',
                height: '26px',
                width: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '2px solid #e8e8e8',
              }}
            >
              Ё
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>Мои чаты</div>
          </div>

          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '14px',
            }}
          >
            МЕНЮ ▼
          </button>
        </div>
      )}

      {/* Menu */}
      {showMenu && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '30px',
            backgroundColor: '#fff',
            border: '2px solid #000',
            zIndex: 50,
            width: '192px',
          }}
        >
          <div
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#000',
            }}
          >
            <KeyIcon />№ {user?.userNumber || 'Загрузка...'}
          </div>

          <button
            onClick={() => {
              alert('Это приложение для чата с анонимными пользователями!');
              setShowMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <QuestionIcon />
            Что это?
          </button>

          <button
            onClick={() => {
              logout();
              setShowMenu(false);
            }}
            style={{
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'Roboto Mono, Sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ExitIcon />
            Выйти
          </button>
        </div>
      )}

      {/* Chat List */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {allChats.map((chat) => (
          <ChatItem
            key={`${chat.id}-${chat.lastMessage?.time || chat.createdAt}`}
            chat={chat}
            isSelected={selectedChatId === chat.id}
            onClick={() => onChatSelect(chat)}
          />
        ))}

        {chats.length === 0 && groupChats.length === 0 && (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              fontFamily: 'monospace',
              color: '#666',
            }}
          >
            <p>У вас пока нет чатов</p>
            <p style={{ fontSize: '12px' }}>Начните общение!</p>
          </div>
        )}
      </div>

      <SearchButtons isDesktop={isDesktop} onRandomSearch={onRandomSearch} onSearchByNumber={onSearchByNumber} />
    </div>
  );
};
